<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { call, identity, type Schemas } from '../api/http'
import { useSession } from '../stores/session'
import { useForm } from '../lib/form'
import { formatDate } from '../i18n'
import UiCard from '../components/UiCard.vue'
import UiButton from '../components/UiButton.vue'
import ErrorBanner from '../components/ErrorBanner.vue'
import EmptyState from '../components/EmptyState.vue'

const route = useRoute()
const router = useRouter()
const session = useSession()
const token = route.params.token as string
const load = useForm()
const accept = useForm()
const info = ref<Schemas['InvitationInfo'] | null>(null)

onMounted(() => load.submit(async () => (info.value = await call(identity.GET('/invitations/{token}', { params: { path: { token } } })))))

const back = computed(() => ({ redirect: route.fullPath, email: info.value?.email }))
const emailMismatch = computed(
  () => session.authenticated && info.value && session.user?.email.toLowerCase() !== info.value.email.toLowerCase(),
)

async function onAccept() {
  if (await accept.submit(() => session.acceptInvitation(token))) router.replace('/')
}
</script>

<template>
  <div class="invitation">
    <p v-if="load.busy.value" class="muted">{{ $t('common.loading') }}</p>

    <EmptyState
      v-else-if="load.error.value"
      :title="$t(load.error.value.status === 410 ? 'invite.goneTitle' : 'invite.invalidTitle')"
      :message="$t(load.error.value.status === 410 ? 'invite.goneText' : 'invite.invalidText')"
    >
      <RouterLink to="/">{{ $t('invite.goHome') }}</RouterLink>
    </EmptyState>

    <UiCard v-else-if="info" :title="$t('invite.title')">
      <div class="stack">
        <p data-testid="invite-summary">
          {{ $t('invite.summary', { inviter: info.inviter, workspace: info.workspace_name, role: $t(`roles.${info.role}`) }) }}
        </p>
        <p class="muted small">
          {{ $t('invite.sentTo', { email: info.email }) }} · {{ $t('invite.expires', { date: formatDate(info.expires_at) }) }}
        </p>
        <ErrorBanner :error="accept.error.value" />

        <template v-if="session.authenticated">
          <p v-if="emailMismatch" class="small warn">{{ $t('invite.mismatch', { email: session.user?.email }) }}</p>
          <UiButton data-testid="accept-invite" :loading="accept.busy.value" @click="onAccept">{{ $t('invite.accept') }}</UiButton>
        </template>
        <div v-else class="row">
          <UiButton @click="router.push({ path: '/register', query: back })">{{ $t('invite.register') }}</UiButton>
          <UiButton variant="outline" @click="router.push({ path: '/login', query: back })">{{ $t('invite.login') }}</UiButton>
        </div>
      </div>
    </UiCard>
  </div>
</template>

<style scoped>
.invitation {
  max-width: 520px;
}
.warn {
  color: var(--warn);
}
</style>
